/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { createDisposer, type IntervalId, logDebug, type Unsubscribe } from '@amazon/vinyl-util'
import type { Player, PlaylistController } from '@amzn/amp-playback'
import { getDeviceConfig } from '@/device/deviceConfig'
import { type KeyBinding, matchesKey } from '@/device/input/KeyMap'
import { isTextEntry, windowEvents } from '@/util/interaction'
import { ctx } from '@/util/context'
import { guardedSkipForward } from '@/playback/skipGuard'
import { previousOrRestart } from './previousOrRestart'
import { withUserSkip } from '@/playback/metrics/userSkipSignal'

const { target, errorHandler } = ctx('mediaKeyBinding')

export interface MediaKeyBindingDeps {
    readonly playerPromise: Promise<Player>
    readonly playlistController: PlaylistController
}

/**
 * The key map's transport keys: play/pause, play, pause, stop, next, previous,
 * and fast-forward / rewind (a tap skips, a hold seeks). They work wherever
 * focus is, short of a text field, so they listen at the window's capture phase.
 */
export function initializeMediaKeyBindings(deps: MediaKeyBindingDeps): Unsubscribe {
    const disposer = createDisposer()
    const { add, dispose } = disposer
    const { playerPromise, playlistController } = deps

    const KEY_HOLD_THRESHOLD = 0.5 // Time (in seconds) to consider a key as "held"

    playerPromise
        .then((player) => {
            if (disposer.disposed) return
            logDebug(target, 'initializing media key bindings')

            const { keyMap } = getDeviceConfig()
            const skipNext = () => {
                if (!guardedSkipForward(playlistController)) return
                withUserSkip('next', () => playlistController.skipNext()).catch(errorHandler)
            }
            const skipPrevious = () => {
                previousOrRestart(player, playlistController).catch(errorHandler)
            }
            const play = () => {
                player.play().catch(errorHandler)
            }
            const pause = () => player.pause()
            const taps: readonly (readonly [KeyBinding, () => void])[] = [
                [keyMap.playPause, () => (player.paused$.value ? play() : pause())],
                [keyMap.play, play],
                [keyMap.pause, pause],
                [keyMap.stop, pause],
                [keyMap.next, skipNext],
                [keyMap.previous, skipPrevious],
            ]

            add(
                keyRepeatHandler({
                    key: keyMap.rewind,
                    onRepeat: (elapsed, _repeat) => {
                        if (elapsed > KEY_HOLD_THRESHOLD) {
                            player.seekTo(player.currentTime$.value - 10).catch(errorHandler)
                        }
                    },
                    onEnd: (elapsed) => {
                        if (elapsed <= KEY_HOLD_THRESHOLD) skipPrevious()
                    },
                })
            )

            add(
                keyRepeatHandler({
                    key: keyMap.fastForward,
                    onRepeat: (elapsed, _repeat) => {
                        if (elapsed > KEY_HOLD_THRESHOLD) {
                            player.seekTo(player.currentTime$.value + 10).catch(errorHandler)
                        }
                    },
                    onEnd: (elapsed) => {
                        if (elapsed <= KEY_HOLD_THRESHOLD) skipNext()
                    },
                })
            )

            add(
                windowEvents.on(
                    'keydown',
                    (e: KeyboardEvent) => {
                        if (isTextEntry(e.target)) return
                        const tap = taps.find(([binding]) => matchesKey(e, binding))
                        if (!tap) return
                        e.preventDefault()
                        if (!e.repeat) tap[1]()
                    },
                    { capture: true }
                )
            )
        })
        .catch(errorHandler)
    return dispose
}

const KEY_REPEAT_INTERVAL_SECONDS = 0.5

function keyRepeatHandler({
    key,
    onRepeat,
    onEnd,
}: {
    key: KeyBinding
    onRepeat: { (elapsed: number, repeat: number): void }
    onEnd: { (elapsed: number): void }
}): Unsubscribe {
    const { add, dispose } = createDisposer()
    let c = 0
    let start = 0
    let intervalId: IntervalId | null = null

    const clear = () => {
        if (intervalId != null) {
            clearInterval(intervalId)
            intervalId = null
        }
    }
    add(clear)
    add(
        windowEvents.on(
            'keydown',
            (e: KeyboardEvent) => {
                if (isTextEntry(e.target)) return
                if (intervalId != null) return
                if (matchesKey(e, key)) {
                    start = Date.now()
                    c = 0
                    intervalId = setInterval(() => {
                        const elapsed = (Date.now() - start) / 1000
                        onRepeat(elapsed, ++c)
                    }, KEY_REPEAT_INTERVAL_SECONDS * 1000)
                    e.preventDefault()
                }
            },
            { capture: true }
        )
    )

    add(
        windowEvents.on(
            'keyup',
            (e: KeyboardEvent) => {
                // Guards against a stale `start` from an unrelated earlier press:
                // without this, a key that never started an interval (editable
                // target, or no matching keydown at all) could still fire `onEnd`.
                if (intervalId == null) return
                if (matchesKey(e, key)) {
                    clear()
                    onEnd((Date.now() - start) / 1000)
                    e.preventDefault()
                }
            },
            { capture: true }
        )
    )

    return dispose
}
